import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from './icons'

/**
 * The admin toast, shared by the console shell and the Album Studio frame so the
 * two hosts never drift. A plain toast auto-dismisses after 2.6s. A toast with
 * actions (TASK-3022: "Undo", "View <song>") stays for 30s by default, pauses
 * while hovered or focused so a keyboard user can reach its buttons, and closes
 * on Escape.
 */

export type ToastTone = 'gold' | 'sage' | 'rose' | 'neutral'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface FlashOptions {
  actions?: ToastAction[]
  /** Override the auto-dismiss delay in ms. */
  durationMs?: number
}

/** Show a toast. `tone` defaults to sage. */
export type FlashFn = (msg: string, tone?: ToastTone, options?: FlashOptions) => void

interface ToastState {
  id: number
  msg: string
  tone: ToastTone
  actions: ToastAction[]
  durationMs: number
}

const PLAIN_MS = 2600
const ACTION_MS = 30_000

// Light variants of the studio tones, chosen for contrast on the dark ink pill;
// the --as-* tokens are tuned for the cream paper.
const TONE_ON_INK: Record<ToastTone, string> = {
  gold: '#e4b549',
  sage: '#8bc17e',
  rose: '#e4a79e',
  neutral: '#c9c0ae',
}

/**
 * Own one toast slot: state, the dismiss timer and its pause.
 *
 * @returns The current toast, `flash` to show one, and the handlers the view needs.
 */
export function useToastHost(): {
  toast: ToastState | null
  flash: FlashFn
  dismiss: () => void
  pause: () => void
  resume: () => void
} {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const current = useRef<ToastState | null>(null)

  const clear = (): void => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }
  const dismiss = useCallback(() => {
    clear()
    current.current = null
    setToast(null)
  }, [])
  const schedule = useCallback((ms: number) => {
    clear()
    timer.current = setTimeout(dismiss, ms)
  }, [dismiss])

  const flash = useCallback<FlashFn>((msg, tone = 'sage', options = {}) => {
    const actions = options.actions ?? []
    const next: ToastState = { id: Date.now(), msg, tone, actions, durationMs: options.durationMs ?? (actions.length ? ACTION_MS : PLAIN_MS) }
    current.current = next
    setToast(next)
    schedule(next.durationMs)
  }, [schedule])

  const pause = useCallback(() => clear(), [])
  const resume = useCallback(() => {
    if (current.current) schedule(current.current.durationMs)
  }, [schedule])

  useEffect(() => () => clear(), [])
  return { toast, flash, dismiss, pause, resume }
}

/**
 * Render the toast slot from {@link useToastHost}.
 *
 * @param host - The value returned by `useToastHost()`.
 */
export function ToastView({ host }: { host: ReturnType<typeof useToastHost> }): JSX.Element | null {
  const { toast, dismiss, pause, resume } = host
  useEffect(() => {
    if (!toast?.actions.length) return undefined
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toast, dismiss])

  if (!toast) return null
  return (
    <div
      key={toast.id}
      role="status"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      className="fixed bottom-6 left-1/2 z-[70] flex max-w-[90vw] -translate-x-1/2 flex-wrap items-center gap-2.5 rounded-xl bg-[var(--as-ink)] px-[18px] py-[11px] text-[13.5px] font-medium text-white shadow-[var(--as-shadow-lg)]"
      style={{ animation: 'as-fade-up .3s ease both' }}
    >
      <span className="flex" style={{ fontSize: toast.tone === 'rose' ? 15 : 16, color: TONE_ON_INK[toast.tone] }}>
        {toast.tone === 'rose' ? <Icon.trash /> : <Icon.check />}
      </span>
      <span>{toast.msg}</span>
      {toast.actions.map((action) => (
        <button
          key={action.label}
          type="button"
          onClick={() => {
            dismiss()
            action.onClick()
          }}
          className="min-h-[32px] rounded-lg border border-white/25 px-3 text-[13px] font-semibold text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--as-gold)]"
        >
          {action.label}
        </button>
      ))}
    </div>
  )
}
